export interface WebGPUCapability {
  supported: boolean
  adapterInfo?: string
}

export interface DeviceCapabilityReport {
  webgpu: boolean
  wasmThreads: boolean
  estimatedMemoryMB: number
  recommendedModel: string | null
  canRunLocal: boolean
  reason?: string
}

type WebGPUAdapterInfo = {
  vendor?: string
  architecture?: string
  device?: string
  description?: string
}

type WebGPUAdapter = {
  info?: WebGPUAdapterInfo
  requestAdapterInfo?: () => Promise<WebGPUAdapterInfo>
}

type NavigatorWithLocalAICapability = Navigator & {
  gpu?: {
    requestAdapter: () => Promise<WebGPUAdapter | null>
  }
  deviceMemory?: number
}

export const LOCAL_AI_MODEL_REQUIREMENTS_MB = {
  'gemma-2-2b-it-q4f16_1-MLC': 3000,
  'gemma-2-2b-it-q4f32_1-MLC': 4500,
  'Llama-3.2-1B-Instruct-q4f16_1-MLC': 1500,
} as const

type LocalAIModelId = keyof typeof LOCAL_AI_MODEL_REQUIREMENTS_MB

const FALLBACK_AVAILABLE_MEMORY_MB = 1024
const RECOMMENDED_MODELS: LocalAIModelId[] = [
  'gemma-2-2b-it-q4f16_1-MLC',
  'Llama-3.2-1B-Instruct-q4f16_1-MLC',
]

const getNavigator = (): NavigatorWithLocalAICapability | null => {
  if (typeof navigator === 'undefined') return null
  return navigator as NavigatorWithLocalAICapability
}

const formatAdapterInfo = async (adapter: WebGPUAdapter): Promise<string | undefined> => {
  const info =
    adapter.info ??
    (adapter.requestAdapterInfo ? await adapter.requestAdapterInfo().catch(() => undefined) : undefined)

  if (!info) return undefined

  const parts = [info.vendor, info.architecture, info.device, info.description].filter(Boolean)
  return parts.length > 0 ? parts.join(' / ') : undefined
}

export const checkWebGPU = async (): Promise<WebGPUCapability> => {
  const currentNavigator = getNavigator()
  if (!currentNavigator?.gpu?.requestAdapter) {
    return { supported: false }
  }

  try {
    const adapter = await currentNavigator.gpu.requestAdapter()
    if (!adapter) {
      return { supported: false }
    }

    const adapterInfo = await formatAdapterInfo(adapter)
    return adapterInfo ? { supported: true, adapterInfo } : { supported: true }
  } catch {
    return { supported: false }
  }
}

export const checkWASMThreads = (): boolean =>
  typeof SharedArrayBuffer !== 'undefined' &&
  (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true

export const estimateAvailableMemoryMB = (): number => {
  const deviceMemoryGB = getNavigator()?.deviceMemory

  if (typeof deviceMemoryGB !== 'number' || !Number.isFinite(deviceMemoryGB) || deviceMemoryGB <= 0) {
    return FALLBACK_AVAILABLE_MEMORY_MB
  }

  return Math.round(deviceMemoryGB * 1024 * 0.4)
}

export const getModelRequirementsMB = (modelId: string): number =>
  LOCAL_AI_MODEL_REQUIREMENTS_MB[modelId as LocalAIModelId] ?? Number.POSITIVE_INFINITY

const selectRecommendedModel = (params: {
  webgpu: boolean
  wasmThreads: boolean
  estimatedMemoryMB: number
}): string | null => {
  const { webgpu, wasmThreads, estimatedMemoryMB } = params

  for (const modelId of RECOMMENDED_MODELS) {
    const requiresWebGPU = modelId.startsWith('gemma-')
    const hasRuntime = requiresWebGPU ? webgpu : webgpu || wasmThreads

    if (hasRuntime && estimatedMemoryMB >= getModelRequirementsMB(modelId)) {
      return modelId
    }
  }

  return null
}

export const assessLocalAICapability = async (): Promise<DeviceCapabilityReport> => {
  const [{ supported: webgpu }, wasmThreads, estimatedMemoryMB] = await Promise.all([
    checkWebGPU(),
    Promise.resolve(checkWASMThreads()),
    Promise.resolve(estimateAvailableMemoryMB()),
  ])

  if (!webgpu && !wasmThreads) {
    return {
      webgpu,
      wasmThreads,
      estimatedMemoryMB,
      recommendedModel: null,
      canRunLocal: false,
      reason: 'WebGPU e WASM Threads indisponiveis neste dispositivo.',
    }
  }

  const recommendedModel = selectRecommendedModel({ webgpu, wasmThreads, estimatedMemoryMB })
  if (!recommendedModel) {
    return {
      webgpu,
      wasmThreads,
      estimatedMemoryMB,
      recommendedModel: null,
      canRunLocal: false,
      reason: 'Memoria estimada insuficiente para os modelos locais suportados.',
    }
  }

  return {
    webgpu,
    wasmThreads,
    estimatedMemoryMB,
    recommendedModel,
    canRunLocal: true,
  }
}
